
























































































































































import { PropType } from 'vue';

import Validatable from '@/mixins/validatable';
import BindsAttrs from '@/mixins/binds-attrs';
import mixins from '@/utils/mixins';

import { get } from 'vuex-pathify';

// helpers
import { deepEqual, searchItems } from '@/utils/helpers';

import { Module } from '@/models/configurator/module';
import { PropValidator } from 'vue/types/options';
import { QuoteProductItem } from '@/models/quote';
import QuoteWizardModuleConfigurationDialog from './QuoteWizardModuleConfigurationDialog.vue';

const baseMixins = mixins(BindsAttrs, Validatable);

interface Options extends InstanceType<typeof baseMixins> {
  /* eslint-disable-next-line camelcase */
  $_modelEvent: string;
}

export type InputValidationRule = (value: any) => string | boolean;

export default baseMixins
  .extend<Options>({
    functional: false
  })
  .extend({
    components: { QuoteWizardModuleConfigurationDialog },
    name: 'QuoteWizardModuleSelector',

    props: {
      id: String,
      products: Array as PropType<QuoteProductItem[]>,
      hideDetails: [Boolean, String] as PropType<boolean | 'auto'>,
      hint: String,
      label: String,
      persistentHint: Boolean,
      editable: { type: Boolean, default: true },
      exchangeRate: Number,
      currencyCode: String,
      snapshot: Number,

      valueComparator: {
        type: Function,
        default: deepEqual
      } as PropValidator<typeof deepEqual>,

      onlyAfterMarketParts: Boolean,
      height: {
        type: String,

        default: 'auto'
      }
    },

    inheritAttrs: false,

    data() {
      return {
        lazyValue: this.products,
        hasMouseDown: false,
        searchModuleQuery: '',

        modulesConfigurations: [] as any,
        selectedIndex: -1
      };
    },

    computed: {
      modules: get<Module[]>('configuratorModule/modules'),

      getModuleById: get<(id: number) => Module>(
        'configuratorModule/getModuleById'
      ),

      computedId(): string {
        return this.id || `input-${(this as any)._uid}`;
      },

      hasDetails(): boolean {
        return this.messagesToDisplay.length > 0;
      },

      hasHint(): boolean {
        return (
          !this.hasMessages &&
          !!this.hint &&
          (this.persistentHint || this.isFocused)
        );
      },

      hasLabel(): boolean {
        return !!(this.$slots.label || this.label);
      },

      internalValue: {
        get(): any[] {
          return this.lazyValue;
        },

        set(val: any) {
          this.lazyValue = val;
          // this.$emit(this.$_modelEvent, val);
        }
      } as any,

      isDirty(): boolean {
        return !!this.lazyValue;
      },

      isLabelActive(): boolean {
        return this.isDirty;
      },

      messagesToDisplay(): string[] {
        if (this.hasHint) return [this.hint];

        if (!this.hasMessages) return [];

        return this.validations
          .map((validation: string | InputValidationRule) => {
            if (typeof validation === 'string') return validation;

            const validationResult = validation(this.internalValue);

            return typeof validationResult === 'string' ? validationResult : '';
          })
          .filter(message => message !== '');
      },

      showDetails(): boolean {
        return (
          this.hideDetails === false ||
          (this.hideDetails === 'auto' && this.hasDetails)
        );
      },

      searchModules(): any[] {
        return searchItems<any>(this.modules, this.searchModuleQuery);
      },

      sortedProductCategories(): QuoteProductItem[] {
        return this.internalValue.filter((product: QuoteProductItem) =>
          this.onlyAfterMarketParts
            ? this.getModuleById(product.module!).is_public
            : product
        );
      }
    },

    watch: {
      products(val) {
        this.lazyValue = val;
      }
    },

    beforeCreate() {
      // v-radio-group needs to emit a different event
      // https://github.com/vuetifyjs/vuetify/issues/4752
      this.$_modelEvent =
        (this.$options.model && this.$options.model.event) || 'input';
    },

    methods: {
      removeModuleConfiguration(product: QuoteProductItem) {
        // remove item
        this.$emit('removeProductItem', product);
      },

      saveModuleConfiguration(product: QuoteProductItem) {
        if (this.selectedIndex === -1) {
          this.addModuleConfiguration(product);
        } else {
          this.editModuleConfiguration(product);
        }
        this.selectedIndex = -1;
      },

      addModuleConfiguration(product: QuoteProductItem) {
        this.$emit('addProductItem', product);
        // to redender component
        this.$forceUpdate();
      },

      editModuleConfiguration(product: QuoteProductItem) {
        this.$emit('updateProductItem', product);

        // this.internalValue.splice(this.selectedIndex, 1, product);
      },

      addModuleConfigurationDialog() {
        this.selectedIndex = -1;
        const wizard = this.$refs.moduleConfigurationDialog as InstanceType<
          typeof QuoteWizardModuleConfigurationDialog
        >;
        return wizard.open(
          undefined,
          this.exchangeRate,
          this.currencyCode,
          true,
          this.snapshot
        );
      },

      editModuleConfigurationDialog(product: QuoteProductItem, index: number) {
        this.selectedIndex = index;

        const wizard = this.$refs.moduleConfigurationDialog as InstanceType<
          typeof QuoteWizardModuleConfigurationDialog
        >;
        return wizard.open(
          product,
          this.exchangeRate,
          this.currencyCode,
          false,
          this.snapshot
        );
      }
    }
  });
